<template>
  <div class="customer-service-container">
    <div class="page-header">
      <h2>在线客服</h2>
      <p class="subtitle">与小程序用户实时沟通 <span :class="['ws-status', wsConnected ? 'connected' : 'disconnected']">{{ wsConnected ? '● 已连接' : '○ 未连接' }}</span></p>
    </div>

    <div class="chat-layout">
      <!-- 左侧会话列表 -->
      <div class="sessions-panel">
        <div class="sessions-header">
          <el-input v-model="searchKeyword" placeholder="搜索用户" prefix-icon="Search" clearable size="small" />
        </div>
        <div class="sessions-list">
          <div 
            v-for="session in filteredSessions" 
            :key="session.id" 
            class="session-item"
            :class="{ 'active': currentSession?.id === session.id }"
            @click="selectSession(session)"
          >
            <el-badge :value="session.unreadCount" :hidden="!session.unreadCount" :max="99">
              <el-avatar :size="44" class="session-avatar">
                {{ session.user?.realName?.charAt(0) || session.user?.mobile?.charAt(0) || 'U' }}
              </el-avatar>
            </el-badge>
            <div class="session-info">
              <div class="session-name">{{ session.user?.realName || session.user?.mobile || '未知用户' }}</div>
              <div class="session-last-message">{{ session.lastMessage || '暂无消息' }}</div>
            </div>
            <div class="session-meta">
              <div class="session-time">{{ formatTime(session.lastMessageAt) }}</div>
              <el-tag :type="statusMap[session.status]?.type" size="small">{{ statusMap[session.status]?.label }}</el-tag>
            </div>
          </div>
          <el-empty v-if="sessions.length === 0" description="暂无会话" />
        </div>
      </div>

      <!-- 右侧聊天区域 -->
      <div class="chat-panel">
        <template v-if="currentSession">
          <div class="chat-header">
            <div class="chat-user-info">
              <el-avatar :size="40" class="user-avatar">
                {{ currentSession.user?.realName?.charAt(0) || currentSession.user?.mobile?.charAt(0) || 'U' }}
              </el-avatar>
              <div class="user-details">
                <div class="user-name">{{ currentSession.user?.realName || currentSession.user?.mobile || '未知用户' }}</div>
                <div class="user-status">
                  <el-tag :type="statusMap[currentSession.status]?.type" size="small">
                    {{ statusMap[currentSession.status]?.label }}
                  </el-tag>
                </div>
              </div>
            </div>
            <div class="chat-actions">
              <el-button v-if="currentSession.status !== 'closed'" type="danger" size="small" @click="closeCurrentSession">
                结束会话
              </el-button>
            </div>
          </div>

          <div class="messages-container" ref="messagesContainer">
            <div 
              v-for="msg in messages" 
              :key="msg.id" 
              class="message-item"
              :class="{ 'my-message': msg.senderType === 'staff' }"
            >
              <div class="message-avatar">
                <el-avatar :size="32">
                  {{ msg.senderType === 'staff' ? '我' : (msg.user?.realName?.charAt(0) || 'U') }}
                </el-avatar>
              </div>
              <div class="message-content">
                <div class="message-header">
                  <span class="sender-name">{{ msg.senderType === 'staff' ? '我' : (msg.user?.realName || msg.user?.mobile) }}</span>
                  <span class="message-time">{{ formatTime(msg.createdAt) }}</span>
                </div>
                <div class="message-body">
                  <template v-if="msg.messageType === 'image'">
                    <el-image 
                      :src="getFileUrl(msg.fileUrl)" 
                      fit="cover" 
                      style="max-width: 200px; max-height: 200px; border-radius: 8px;"
                      :preview-src-list="[getFileUrl(msg.fileUrl)]"
                    />
                  </template>
                  <template v-else-if="msg.messageType === 'file'">
                    <a :href="getFileUrl(msg.fileUrl)" target="_blank" class="file-link">
                      <el-icon><Document /></el-icon>
                      <span>{{ msg.fileName || '下载文件' }}</span>
                    </a>
                  </template>
                  <template v-else>
                    <span>{{ msg.message }}</span>
                  </template>
                </div>
              </div>
            </div>
            <div ref="bottomAnchor"></div>
          </div>

          <div class="input-area">
            <div class="input-toolbar">
              <el-upload
                :show-file-list="false"
                :before-upload="beforeUploadImage"
                accept="image/*"
              >
                <el-button type="primary" text>
                  <el-icon><Picture /></el-icon>
                </el-button>
              </el-upload>
              <el-upload
                :show-file-list="false"
                :before-upload="beforeUploadFile"
              >
                <el-button type="primary" text>
                  <el-icon><Document /></el-icon>
                </el-button>
              </el-upload>
            </div>
            <el-input
              v-model="inputMessage"
              placeholder="输入消息..."
              @keyup.enter="sendMessage"
              class="message-input"
            />
            <el-button type="primary" @click="sendMessage" :disabled="!inputMessage.trim()">发送</el-button>
          </div>
        </template>
        <el-empty v-else description="请选择一个会话" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Picture, Document } from '@element-plus/icons-vue'
import request from '@/api/index.js'

const statusMap = {
  waiting: { label: '等待中', type: 'warning' },
  active: { label: '进行中', type: 'success' },
  closed: { label: '已关闭', type: 'info' }
}

const sessions = ref([])
const currentSession = ref(null)
const messages = ref([])
const inputMessage = ref('')
const searchKeyword = ref('')
const wsConnected = ref(false)
const socket = ref(null)
const messagesContainer = ref(null)
const bottomAnchor = ref(null)

const filteredSessions = computed(() => {
  if (!searchKeyword.value) return sessions.value
  const keyword = searchKeyword.value.toLowerCase()
  return sessions.value.filter(s => 
    s.user?.realName?.toLowerCase().includes(keyword) ||
    s.user?.mobile?.includes(keyword)
  )
})

const loadSessions = async () => {
  try {
    const res = await request.get('/admin/customer-service/sessions', { params: { pageSize: 100 } })
    if (res.code === 200) {
      sessions.value = res.data.items || []
    }
  } catch (err) {
    console.error('加载会话失败:', err)
  }
}

const selectSession = async (session) => {
  currentSession.value = session
  try {
    const res = await request.get(`/admin/customer-service/sessions/${session.id}/messages`, { params: { pageSize: 100 } })
    if (res.code === 200) {
      messages.value = res.data.items || []
      scrollToBottom()
    }
  } catch (err) {
    console.error('加载消息失败:', err)
  }
}

const closeCurrentSession = async () => {
  if (!currentSession.value) return
  try {
    await request.put(`/admin/customer-service/sessions/${currentSession.value.id}/close`)
    ElMessage.success('会话已关闭')
    currentSession.value.status = 'closed'
    loadSessions()
  } catch (err) {
    ElMessage.error('操作失败')
  }
}

const initSocket = () => {
  const token = localStorage.getItem('admin-token')
  const adminInfo = JSON.parse(localStorage.getItem('admin-info') || '{}')
  
  if (!token || !adminInfo?.id) {
    ElMessage.warning('请先登录')
    return
  }
  
  const wsUrl = `wss://tory.ffdzheng.com/ws/customer-service?userId=${adminInfo?.id}&userType=staff&token=${token}`
  
  socket.value = new WebSocket(wsUrl)
  
  socket.value.onopen = () => {
    console.log('WebSocket已连接')
    wsConnected.value = true
  }
  
  socket.value.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data)
      handleSocketMessage(msg)
    } catch (e) {
      console.error('解析消息失败:', e)
    }
  }
  
  socket.value.onclose = () => {
    console.log('WebSocket已断开')
    wsConnected.value = false
    setTimeout(() => {
      if (!wsConnected.value) initSocket()
    }, 3000)
  }
  
  socket.value.onerror = (err) => {
    console.error('WebSocket错误:', err)
    wsConnected.value = false
  }
}

const sendSocketMessage = (event, data) => {
  if (socket.value && socket.value.readyState === WebSocket.OPEN) {
    socket.value.send(JSON.stringify({ event, data }))
  } else {
    ElMessage.warning('WebSocket未连接')
  }
}

const handleSocketMessage = (msg) => {
  const { event, data } = msg
  
  switch (event) {
    case 'connected':
      wsConnected.value = true
      break
      
    case 'message:receive':
    case 'message:sent':
      if (currentSession.value && data.userId === currentSession.value.userId) {
        const exists = messages.value.some(m => m.id === data.id)
        if (!exists) {
          messages.value.push(data)
          scrollToBottom()
        }
      }
      // 刷新会话列表
      loadSessions()
      break
      
    case 'session:new':
      // 新用户接入
      loadSessions()
      ElMessage.info('有新用户接入客服')
      break
      
    case 'staff:online':
      // 在线客服数量
      break
  }
}

const sendMessage = () => {
  if (!inputMessage.value.trim() || !currentSession.value) return
  if (!wsConnected.value) {
    ElMessage.warning('WebSocket未连接')
    return
  }
  
  sendSocketMessage('message:send', {
    message: inputMessage.value.trim(),
    messageType: 'text',
    targetUserId: currentSession.value.userId
  })
  
  inputMessage.value = ''
}

const beforeUploadImage = async (file) => {
  const formData = new FormData()
  formData.append('file', file)
  try {
    const res = await request.post('/customer-service/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    if (res.code === 200) {
      sendSocketMessage('message:send', {
        message: '[图片]',
        messageType: 'image',
        fileUrl: res.data.url,
        fileName: res.data.fileName,
        fileSize: res.data.fileSize,
        targetUserId: currentSession.value.userId
      })
    }
  } catch (err) {
    ElMessage.error('上传失败')
  }
  return false
}

const beforeUploadFile = async (file) => {
  const formData = new FormData()
  formData.append('file', file)
  try {
    const res = await request.post('/customer-service/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    if (res.code === 200) {
      sendSocketMessage('message:send', {
        message: '[文件]',
        messageType: 'file',
        fileUrl: res.data.url,
        fileName: res.data.fileName,
        fileSize: res.data.fileSize,
        targetUserId: currentSession.value.userId
      })
    }
  } catch (err) {
    ElMessage.error('上传失败')
  }
  return false
}

const getFileUrl = (url) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return 'https://tory.ffdzheng.com' + url
}

const scrollToBottom = () => {
  nextTick(() => {
    if (bottomAnchor.value) {
      bottomAnchor.value.scrollIntoView({ behavior: 'smooth' })
    }
  })
}

const formatTime = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const h = date.getHours().toString().padStart(2, '0')
  const m = date.getMinutes().toString().padStart(2, '0')
  if (isToday) return `${h}:${m}`
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  return `${month}-${day} ${h}:${m}`
}

onMounted(() => {
  loadSessions()
  initSocket()
})

onUnmounted(() => {
  if (socket.value) {
    socket.value.close()
  }
})
</script>

<style scoped>
.customer-service-container {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-header h2 {
  margin: 0;
}

.subtitle {
  color: #666;
  font-size: 14px;
}

.ws-status {
  margin-left: 8px;
  font-size: 12px;
}

.ws-status.connected {
  color: #67c23a;
}

.ws-status.disconnected {
  color: #f56c6c;
}

.chat-layout {
  flex: 1;
  display: flex;
  gap: 16px;
  min-height: 0;
}

.sessions-panel {
  width: 320px;
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
}

.sessions-header {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.sessions-list {
  flex: 1;
  overflow-y: auto;
}

.session-item {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;
}

.session-item:hover {
  background: #f5f7fa;
}

.session-item.active {
  background: #ecf5ff;
}

.session-avatar {
  margin-right: 12px;
}

.session-info {
  flex: 1;
  min-width: 0;
}

.session-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.session-last-message {
  font-size: 12px;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-meta {
  text-align: right;
}

.session-time {
  font-size: 11px;
  color: #999;
  margin-bottom: 4px;
}

.chat-panel {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #eee;
}

.chat-user-info {
  display: flex;
  align-items: center;
}

.user-avatar {
  margin-right: 12px;
}

.user-name {
  font-weight: 500;
  margin-bottom: 4px;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.message-item {
  display: flex;
  margin-bottom: 16px;
}

.message-item.my-message {
  flex-direction: row-reverse;
}

.message-avatar {
  margin: 0 8px;
}

.message-content {
  max-width: 70%;
}

.message-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  gap: 8px;
}

.my-message .message-header {
  flex-direction: row-reverse;
}

.sender-name {
  font-size: 12px;
  color: #666;
}

.message-time {
  font-size: 11px;
  color: #999;
}

.message-body {
  background: #f5f5f5;
  padding: 10px 14px;
  border-radius: 12px;
  word-break: break-word;
}

.my-message .message-body {
  background: #409eff;
  color: #fff;
}

.file-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #409eff;
  text-decoration: none;
}

.my-message .file-link {
  color: #fff;
}

.input-area {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #eee;
  gap: 8px;
}

.input-toolbar {
  display: flex;
  gap: 4px;
}

.message-input {
  flex: 1;
}
</style>
